/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { SelectFactory, SelectTypes, type Option } from '@g17eco/molecules/select/SelectFactory';
import { DataTransferMappingStatus } from '@g17eco/types/data-transfer';
import type { Filters } from './data-transfer-utils';

interface FilterSelectProps {
  label: string;
  paramKey: keyof Filters;
  value: string;
  options: Option<string>[];
  onSelect: (key: keyof Filters, value: string) => void;
}

const ALL_OPTION: Option<string> = { label: 'All', value: '' };

const FilterSelect = ({ label, paramKey, value, options, onSelect }: FilterSelectProps) => (
  <div>
    <small className='text-ThemeTextLight d-block mb-1'>{label}</small>
    <SelectFactory<string>
      selectType={SelectTypes.SingleSelect}
      options={[ALL_OPTION, ...options]}
      value={options.find((o) => o.value === value) ?? ALL_OPTION}
      onChange={(selected) => onSelect(paramKey, selected?.value ?? '')}
      size='sm'
    />
  </div>
);

const CONNECTION_OPTIONS: Option<string>[] = [
  { value: 'connected', label: 'Has connection' },
  { value: 'missing', label: 'No connection' },
];

const SURVEY_OPTIONS: Option<string>[] = [
  { value: 'value', label: 'Has value' },
  { value: 'missing', label: 'Missing value' },
];

const STATUS_OPTIONS: Option<string>[] = [
  { value: DataTransferMappingStatus.Active, label: 'Active' },
  { value: DataTransferMappingStatus.Pending, label: 'Pending' },
  { value: DataTransferMappingStatus.NoData, label: 'No Provider Data' },
  { value: 'verified', label: 'Verified' },
];

const VALIDATION_OPTIONS: Option<string>[] = [
  { value: 'errors', label: 'Has errors' },
  { value: 'warnings', label: 'Has warnings' },
  { value: 'discrepancy', label: 'Mismatched values' },
  { value: 'notes', label: 'Has internal notes' },
  { value: 'partner_notes', label: 'Has partner notes' },
  { value: 'clean', label: 'Clean' },
];

interface DataTransferFilterBarProps {
  search: string;
  onSearch: (value: string) => void;
  filters: Filters;
  onSelect: (key: keyof Filters, value: string) => void;
  onClear: () => void;
  totalRows: number;
  filteredRows: number;
}

export const DataTransferFilterBar = ({ search, onSearch, filters, onSelect, onClear, totalRows, filteredRows }: DataTransferFilterBarProps) => {
  const [localSearch, setLocalSearch] = useState(search);
  const debouncedSearch = useDebouncedCallback((value: string) => onSearch(value), 300);
  const hasActiveFilters = Boolean(search || Object.values(filters).some(Boolean));

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    debouncedSearch(value);
  };

  return (
    <div className='d-flex align-items-end gap-3 mb-3 flex-wrap'>
      <div>
        <small className='text-ThemeTextLight d-block mb-1'>Search</small>
        <input
          type='text'
          className='form-control form-control-sm'
          placeholder='Code or name...'
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ minWidth: '180px' }}
        />
      </div>
      <FilterSelect label='Connection' paramKey='connection' value={filters.connection} options={CONNECTION_OPTIONS} onSelect={onSelect} />
      <FilterSelect label='Survey' paramKey='survey' value={filters.survey} options={SURVEY_OPTIONS} onSelect={onSelect} />
      <FilterSelect label='Status' paramKey='status' value={filters.status} options={STATUS_OPTIONS} onSelect={onSelect} />
      <FilterSelect label='Validation' paramKey='validation' value={filters.validation} options={VALIDATION_OPTIONS} onSelect={onSelect} />
      {hasActiveFilters && (
        <div className='d-flex align-items-center gap-2'>
          <small className='text-ThemeTextLight'>{filteredRows}/{totalRows}</small>
          <button className='btn btn-link btn-sm p-0 text-ThemeDangerMedium' onClick={onClear}>
            <i className='fa fa-times me-1' />Clear
          </button>
        </div>
      )}
    </div>
  );
};
